// TodayBanner — surfaces three things at the top of the Home page:
//   1. A "Happy {Holiday}" greeting whenever today is a recognized
//      holiday (per /api/today — see backend/holidays.py). The user
//      explicitly opted Halloween OUT.
//   2. A holiday-themed Bible verse under the greeting.
//   3. A "Today in Black History" rail of curated film cards from
//      the 320-title foundational catalog. By default the rail is
//      rendered INLINE here at the top, but Home.jsx now imports
//      and places <TodayBlackCanonRail /> separately after the
//      Featured Room section. Pass slot="holidayOnly" to suppress
//      the inline rail, or slot="canonOnly" to render JUST the
//      rail. User asked 2026-05-22 to move the Black canon block
//      below the "church stuff" (Featured Room) on Home.
//
// Notification opt-in: the banner includes a one-tap "Notify me on
// holidays" toggle that asks for Notification permission. Once granted
// and on every subsequent visit, if today is a recognized holiday and
// we haven't already pinged the user today, we fire a local
// browser notification with the same greeting + verse. No server-side
// push infrastructure needed — works as a PWA install too.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkle, FilmStrip, ArrowRight, X, BellSimple, BellSimpleSlash, BookOpenText, Play, Pause, Spinner, Lightbulb } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import MediaThumb from "./MediaThumb";
import { playOnce, stopOnce, onPlaybackEvent } from "../lib/singleAudio";
import { getActiveHdVoice } from "../lib/activeVoice";
import useCaptionTranslation from "../hooks/useCaptionTranslation";
import { useAudio } from "../context/AudioContext";
import Tr from "./Tr";

const STORAGE_KEY    = "hw_today_banner_dismissed";
const NOTIFY_OPT_KEY = "hw_holiday_notify_optin";       // "1" once user opts in
const NOTIFY_FIRED   = "hw_holiday_notify_fired_for";   // day-key we've already pinged for

function dayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

const ACCENT = {
  forest:     { bg: "bg-forest",     text: "text-cream", chip: "bg-cream/15 text-cream", quote: "bg-cream/10 text-cream border-cream/20" },
  terracotta: { bg: "bg-terracotta", text: "text-cream", chip: "bg-cream/15 text-cream", quote: "bg-cream/10 text-cream border-cream/20" },
  amber:      { bg: "bg-amber-500",  text: "text-ink",   chip: "bg-ink/10 text-ink",     quote: "bg-ink/5 text-ink border-ink/15" },
  rose:       { bg: "bg-rose-500",   text: "text-cream", chip: "bg-cream/15 text-cream", quote: "bg-cream/10 text-cream border-cream/20" },
};

// Fires a local Notification with the day's greeting + verse. We use
// the basic browser Notification API (not the full Push protocol)
// because:
//   • It works without a VAPID key or server fan-out
//   • It still fires through the PWA shell when the user has the app
//     installed and the page is open in the background tab
//   • Permission is held per-origin so opting in once covers every visit
function fireHolidayNotification(holiday) {
  if (!("Notification" in window)) return false;
  if (Notification.permission !== "granted") return false;
  try {
    const body = holiday.verse
      ? `${holiday.verse.text}  — ${holiday.verse.reference}`
      : holiday.name;
    const notif = new Notification(holiday.greeting, {
      body,
      icon: "/icons/holywords-192.png",
      tag: `hw-holiday-${holiday.key}`,
      silent: false,
    });
    notif.onclick = () => { try { window.focus(); notif.close(); } catch (_) {} };
    localStorage.setItem(NOTIFY_FIRED, dayKey());
    return true;
  } catch (_) { return false; }
}

export default function TodayBanner({ slot = "all" }) {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [optedIn, setOptedIn] = useState(() => {
    try {
      return localStorage.getItem(NOTIFY_OPT_KEY) === "1" &&
        (typeof Notification !== "undefined" ? Notification.permission === "granted" : false);
    } catch (_) { return false; }
  });

  // AI devotional — lazy-loaded only when the user taps "Play".
  const [devotional, setDevotional] = useState(null);    // { text, verse, holiday } once fetched
  const [loadingDev, setLoadingDev] = useState(false);
  const [devPlaying, setDevPlaying] = useState(false);
  // Generic passage player — used for holiday message + any future
  // tap-to-listen surface on this banner. `passagePlaying` is the
  // id of the passage currently in progress (or null). We reuse the
  // AudioContext.playOpenAi pipeline so a holiday + the devotional
  // can't double up. Feb 2026 owner request — "the honoring-me-on-
  // D-Day and every other holiday message should be read aloud."
  const [passagePlaying, setPassagePlaying] = useState(null);
  const playPassage = async (text, id) => {
    if (!text) return;
    if (passagePlaying === id) {
      // Tapping the same passage again pauses it.
      try { window.dispatchEvent(new CustomEvent("hw:audio:stop")); } catch (_) { /* noop */ }
      setPassagePlaying(null);
      return;
    }
    try {
      setPassagePlaying(id);
      const res = await api.post("/tts/speak", { text, voice: getActiveHdVoice() }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      a.onended = () => setPassagePlaying((cur) => (cur === id ? null : cur));
      a.onerror = () => setPassagePlaying((cur) => (cur === id ? null : cur));
      await a.play();
    } catch (e) {
      setPassagePlaying(null);
      toast.error("Could not read aloud. Try again.");
    }
  };
  // Cached blob URL so the second tap resumes instantly without
  // re-hitting OpenAI TTS.
  const devUrlRef = React.useRef(null);

  // Black History Month — pioneer of the day. Fetched once if today
  // is in February; otherwise the rail is hidden.
  const [pioneer, setPioneer] = useState(null);
  const [loadingPioneer, setLoadingPioneer] = useState(false);
  const [pioneerPlaying, setPioneerPlaying] = useState(false);
  const pioneerUrlRef = React.useRef(null);

  // ── Translation hooks (MUST be declared before any early returns
  // per React's rules-of-hooks). Pull the current UI language from
  // AudioContext and translate the holiday verse / greeting / name
  // so the banner reads in the user's selected language. The hooks
  // are no-ops when `holiday` hasn't loaded yet (empty input → empty
  // output), so it's safe to call them unconditionally up here. ──
  const { language: uiLanguage } = useAudio();
  const _holiday = data?.holiday || null;
  const translatedVerseText = useCaptionTranslation(_holiday?.verse?.text || "", uiLanguage);
  const translatedGreeting   = useCaptionTranslation(_holiday?.greeting || "",    uiLanguage);
  const translatedName       = useCaptionTranslation(_holiday?.name || "",        uiLanguage);
  // Devotional body is the longest dynamic block on Home. Pass it
  // through the same caption translator so it ALSO flips to the
  // user's selected language. Without this the body stays in
  // English even after the surrounding chrome translates.
  const translatedDevoText = useCaptionTranslation(devotional?.text || "", uiLanguage);

  useEffect(() => {
    let alive = true;
    // Allow ?previewDate=YYYY-MM-DD on the URL so admins/pastors (and
    // the testing agent) can preview a future holiday/Feb day without
    // waiting for the wall clock. The backend already supports this.
    const params = new URLSearchParams(window.location.search);
    const preview = params.get("previewDate");
    // Fall back to the user's LOCAL date (not server UTC) so a US
    // Eastern viewer at 8 PM on May 21 doesn't see May 22 content.
    // Reported as: "It's may 22nd the daily card for on this day in
    // black history needs to be adjusted — it's a day early."
    // App owner runs from California — pin every viewer to Pacific
    // time so everyone sees the same day's card.
    let localISO = null;
    try {
      const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/Los_Angeles",
        year: "numeric", month: "2-digit", day: "2-digit",
      }).format(new Date());
      if (/^\d{4}-\d{2}-\d{2}$/.test(fmt)) localISO = fmt;
    } catch (_) {}
    if (!localISO) {
      try {
        const dN = new Date();
        const y = dN.getFullYear();
        const m = String(dN.getMonth() + 1).padStart(2, "0");
        const day = String(dN.getDate()).padStart(2, "0");
        localISO = `${y}-${m}-${day}`;
      } catch (_) {}
    }
    const effective = preview || localISO;
    const qs = effective ? `?date=${encodeURIComponent(effective)}` : "";
    api.get(`/today${qs}`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData({ holiday: null, history: null, picks: [] }); });
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (seen === dayKey()) setHidden(true);
    } catch (_) {}
    return () => { alive = false; };
  }, []);

  // Auto-fetch the Black-Inventor-of-the-day card during February. The
  // endpoint 404s outside Feb, so we just swallow that quietly — the rail
  // stays hidden the other 11 months.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const preview = params.get("previewDate");
    let month;
    if (preview) {
      const m = preview.match(/^\d{4}-(\d{2})-\d{2}$/);
      month = m ? parseInt(m[1], 10) - 1 : null;
    } else {
      month = new Date().getMonth();
    }
    if (month !== 1) return; // Feb is month index 1
    let alive = true;
    setLoadingPioneer(true);
    const qs = preview ? `?date_iso=${encodeURIComponent(preview)}` : "";
    api.get(`/today/pioneer${qs}`)
      .then((r) => { if (alive) setPioneer(r.data); })
      .catch(() => { if (alive) setPioneer(null); })
      .finally(() => { if (alive) setLoadingPioneer(false); });
    return () => { alive = false; };
  }, []);

  // Release any object-URLs we created when the component unmounts —
  // otherwise the MP3 blobs leak until page reload. Also listen for
  // the global "another audio took over" signal and reset our UI.
  useEffect(() => {
    const onStart = (e) => {
      const id = e?.detail?.id;
      if (id !== "today-dev") setDevPlaying(false);
      if (id !== "today-pioneer") setPioneerPlaying(false);
    };
    window.addEventListener("hw:read-aloud:start", onStart);
    const off = onPlaybackEvent((evt) => {
      if (evt === "pause" || evt === "ended" || evt === "error") {
        setDevPlaying(false);
        setPioneerPlaying(false);
      }
    });
    return () => {
      window.removeEventListener("hw:read-aloud:start", onStart);
      off();
      try {
        if (devUrlRef.current) URL.revokeObjectURL(devUrlRef.current);
      } catch (_) {}
      try {
        if (pioneerUrlRef.current) URL.revokeObjectURL(pioneerUrlRef.current);
      } catch (_) {}
    };
  }, []);

  // Auto-fire the daily holiday notification if (a) the user has opted
  // in, (b) today is a holiday, and (c) we haven't already pinged today.
  useEffect(() => {
    if (!data || !data.holiday || !optedIn) return;
    try {
      if (localStorage.getItem(NOTIFY_FIRED) === dayKey()) return;
    } catch (_) {}
    fireHolidayNotification(data.holiday);
  }, [data, optedIn]);

  if (!data) return null;
  const { holiday, history, picks } = data;
  const accent = ACCENT[holiday?.color] || ACCENT.terracotta;

  const dismiss = () => {
    setHidden(true);
    try { localStorage.setItem(STORAGE_KEY, dayKey()); } catch (_) {}
  };

  const toggleNotify = async () => {
    if (!("Notification" in window)) {
      toast.error("This browser doesn't support notifications");
      return;
    }
    if (optedIn) {
      try { localStorage.setItem(NOTIFY_OPT_KEY, "0"); } catch (_) {}
      setOptedIn(false);
      toast.success("Holiday reminders off");
      return;
    }
    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }
    if (perm !== "granted") {
      toast.error("Permission denied — enable in browser settings");
      return;
    }
    try { localStorage.setItem(NOTIFY_OPT_KEY, "1"); } catch (_) {}
    setOptedIn(true);
    // Confirm with a one-shot "you're in" notification.
    try {
      new Notification("You're in", { body: "We'll greet you every holiday morning with a verse.", icon: "/icons/holywords-192.png" });
    } catch (_) {}
    // And, if today is already a holiday, fire it right now.
    if (holiday) fireHolidayNotification(holiday);
    toast.success("Holiday reminders on");
  };

  // ── AI devotional: fetch the text + stream it through the studio
  //    TTS endpoint. Audio plays through the global singleton so it
  //    can't overlap with the chapter reader, sibling cards, or the
  //    morning briefing.
  const playDevotional = async () => {
    // Toggle off mid-play.
    if (devPlaying) {
      stopOnce();
      setDevPlaying(false);
      return;
    }
    if (loadingDev) return;
    setLoadingDev(true);
    try {
      const params = new URLSearchParams(window.location.search);
      const preview = params.get("previewDate");
      const qs = preview ? `?date_iso=${encodeURIComponent(preview)}` : "";
      const r = await api.get(`/today/devotional${qs}`);
      setDevotional(r.data);
      const tts = await api.post(
        "/tts/speak",
        { text: r.data.text, voice: getActiveHdVoice(), model: "tts-1", speed: 1.0 },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(tts.data);
      devUrlRef.current = url;
      // Singleton playback — stops any other ReadAloudButton, the
      // chapter reader, or sibling Today cards before starting.
      window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "today-dev", label: "Today's devotional" } }));
      await playOnce(url);
      setDevPlaying(true);
    } catch (e) {
      toast.error(e?.response?.status === 503
        ? "AI is not configured yet"
        : "Couldn't load devotional — try again in a moment");
    } finally {
      setLoadingDev(false);
    }
  };

  // Black-Inventor-of-the-day audio — same TTS pipeline, separate buffer.
  const playPioneer = async () => {
    if (!pioneer?.story) return;
    if (pioneerPlaying) {
      stopOnce();
      setPioneerPlaying(false);
      return;
    }
    try {
      let url = pioneerUrlRef.current;
      if (!url) {
        const tts = await api.post(
          "/tts/speak",
          { text: `${pioneer.name}. ${pioneer.story}`, voice: getActiveHdVoice(), model: "tts-1", speed: 1.0 },
          { responseType: "blob" }
        );
        url = URL.createObjectURL(tts.data);
        pioneerUrlRef.current = url;
      }
      window.dispatchEvent(new CustomEvent("hw:read-aloud:start", { detail: { id: "today-pioneer", label: pioneer?.name ? `Someone you should know · ${pioneer.name}` : "Someone you should know" } }));
      await playOnce(url);
      setPioneerPlaying(true);
    } catch (_) {
      toast.error("Couldn't load the audio — try again");
    }
  };

  // Translation hooks already declared at the top of the function
  // — `translatedVerseText`, `translatedGreeting`, `translatedName`.
  // (They were moved up there to satisfy React's rules-of-hooks;
  // calling them here after the early returns above crashed CRA's
  // ESLint check and broke the production build. See the function
  // body's opening hook block for the active calls.)

  return (
    <>
      {slot !== "canonOnly" && holiday && !hidden && (
        <div
          data-testid="today-holiday-banner"
          className={`${accent.bg} ${accent.text} rounded-3xl px-5 py-5 md:px-7 md:py-6 mb-8 relative overflow-hidden`}
        >
          {/* Whole holiday message (greeting + name + verse) is a single
              tap target that reads everything aloud. Feb 2026 owner
              request: "the honoring me on D-Day and every other
              holiday message should be read aloud." No color pill — the
              text block itself is the affordance, just like the
              devotional below. */}
          <button
            type="button"
            onClick={() => playPassage([
              translatedGreeting || holiday.greeting,
              translatedName || holiday.name,
              holiday.verse ? `${translatedVerseText || holiday.verse.text}. ${holiday.verse.reference}.` : "",
            ].filter(Boolean).join(" · "), "holiday")}
            data-testid="today-holiday-read"
            className="block w-full text-left group focus:outline-none"
            aria-label="Read holiday message"
          >
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase font-bold opacity-90">
              {passagePlaying === "holiday"
                ? <Pause size={10} weight="bold" />
                : <Play size={10} weight="fill" className="group-hover:scale-125 transition-transform" />}
              <Tr>{holiday.month_long ? "This month — tap to listen" : "Today — tap to listen"}</Tr>
            </span>
            <h2 className="font-heading text-3xl md:text-5xl leading-tight mt-3" data-testid="today-holiday-greeting" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.25)" }}>
              {translatedGreeting || holiday.greeting}
            </h2>
            <p className="text-sm md:text-base opacity-90 mt-2 max-w-2xl font-medium" style={{ textShadow: "0 1px 2px rgba(0,0,0,0.22)" }}>
              {translatedName || holiday.name}
              {holiday.key === "black_history_month" && (
                <> — <Tr>every day this month we surface a different chapter of the story.</Tr></>
              )}
            </p>

            {holiday.verse && (
              <blockquote
                data-testid="today-holiday-verse"
                className={`mt-4 px-4 py-3 rounded-2xl border ${accent.quote} max-w-2xl flex gap-3 items-start`}
                style={{ textShadow: "0 1px 2px rgba(0,0,0,0.22)" }}
              >
                <BookOpenText size={20} weight="duotone" className="shrink-0 mt-0.5 opacity-80" />
                <div>
                  <p className="italic text-sm md:text-base leading-relaxed font-medium">"{translatedVerseText || holiday.verse.text}"</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase font-bold opacity-90 mt-1">{holiday.verse.reference}</p>
                </div>
              </blockquote>
            )}
          </button>

          {/* Devotional play affordance + body — Feb 2026 owner request:
              "no more colored pills, just the word — push the word
              and it reads." We render the devotional text itself as
              the tap target. Mobile-friendly (big hit area), no
              redundant pill button. Bell-reminder pill stays since
              it's a distinct intent (subscribe to push reminders). */}
          {devotional?.text && (
            <button
              type="button"
              onClick={playDevotional}
              disabled={loadingDev}
              data-testid="today-devotional-play"
              className={`group mt-4 px-4 py-3 rounded-2xl border ${accent.quote} max-w-2xl text-left transition-all hover:shadow-lg disabled:opacity-70 block w-full`}
              aria-label={loadingDev ? "Loading devotional" : devPlaying ? "Pause devotional" : "Play devotional"}
            >
              {/* Tiny inline status row — sits above the body text.
                  Shows "TAP TO LISTEN" by default, swaps to PLAYING /
                  PAUSED states as the audio runs. The inline text
                  keeps the affordance discoverable without a bright
                  pill button. Bold + drop-shadow guarantees the
                  label survives Android's blue-light filter (which
                  warm-tints the entire screen and crushes pastel
                  text against pastel cards). */}
              <span className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold opacity-90 mb-2">
                {loadingDev ? (
                  <Spinner size={10} weight="bold" className="animate-spin" />
                ) : devPlaying ? (
                  <Pause size={10} weight="bold" className="text-amber-300" />
                ) : (
                  <Play size={10} weight="fill" className="text-amber-300 group-hover:scale-125 transition-transform" />
                )}
                <Tr>{loadingDev ? "Loading…" : devPlaying ? "Playing · tap to pause" : "Tap to listen"}</Tr>
              </span>
              <span
                className="block text-sm md:text-base leading-relaxed whitespace-pre-wrap font-medium"
                style={{
                  // High-contrast drop shadow so cream / white text
                  // on dark or color-card backgrounds remains legible
                  // under display warm filters (Android Bedtime / iOS
                  // Night Shift). Without this, blue-light mode made
                  // the green card text fade into the card itself.
                  textShadow: "0 1px 2px rgba(0,0,0,0.45)",
                }}
              >
                {translatedDevoText || devotional.text}
              </span>
            </button>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              onClick={toggleNotify}
              data-testid="today-holiday-notify"
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wider uppercase opacity-90 hover:opacity-100 hover:underline underline-offset-4 transition-opacity"
            >
              {optedIn ? <BellSimpleSlash size={12} weight="bold" /> : <BellSimple size={12} weight="bold" />}
              {optedIn ? <Tr>Reminders on</Tr> : <Tr>Notify me on holidays</Tr>}
            </button>
          </div>

          <button
            onClick={dismiss}
            data-testid="today-holiday-dismiss"
            aria-label="Dismiss banner for today"
            className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-white/15 hover:bg-white/25"
          >
            <X size={12} weight="bold" />
          </button>
        </div>
      )}

      {slot !== "canonOnly" && pioneer && (
        <section className="mb-10" data-testid="today-pioneer-rail">
          <div className="bg-ink text-cream rounded-3xl px-5 py-5 md:px-7 md:py-6 overflow-hidden relative">
            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase font-bold bg-amber-500/20 text-amber-300 px-2.5 py-1 rounded-full">
              <Lightbulb size={11} weight="fill" /> Black History Month · Inventor of the day
            </span>
            <h2 className="font-heading text-2xl md:text-4xl leading-tight mt-3" data-testid="today-pioneer-name">
              {pioneer.name}
            </h2>
            <p className="text-sm md:text-base opacity-90 mt-2 max-w-3xl" data-testid="today-pioneer-blurb">
              {pioneer.blurb}
            </p>
            {pioneer.story && (
              <p
                data-testid="today-pioneer-story"
                className="text-sm md:text-base opacity-80 mt-3 max-w-3xl leading-relaxed whitespace-pre-wrap"
              >
                {pioneer.story}
              </p>
            )}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <button
                onClick={playPioneer}
                data-testid="today-pioneer-play"
                className="inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-[11px] font-semibold tracking-wider uppercase bg-amber-500 text-ink hover:bg-amber-400 transition-colors"
              >
                {pioneerPlaying ? <Pause size={12} weight="bold" /> : <Play size={12} weight="fill" />}
                {pioneerPlaying ? "Pause story" : "Hear their story"}
              </button>
            </div>
          </div>
        </section>
      )}

      {slot !== "canonOnly" && loadingPioneer && !pioneer && (() => {
        const params = new URLSearchParams(window.location.search);
        const preview = params.get("previewDate");
        if (preview) {
          const m = preview.match(/^\d{4}-(\d{2})-\d{2}$/);
          return m && parseInt(m[1], 10) === 2;
        }
        return new Date().getMonth() === 1;
      })() && (
        <section className="mb-10" data-testid="today-pioneer-loading">
          <div className="bg-ink/90 text-cream rounded-3xl px-5 py-5 md:px-7 md:py-6 flex items-center gap-3">
            <Spinner size={18} weight="bold" className="animate-spin opacity-70" />
            <span className="text-sm opacity-80">Loading today's Black inventor…</span>
          </div>
        </section>
      )}

      {slot !== "holidayOnly" && history && picks && picks.length > 0 && (
        <section className="mb-10" data-testid="today-history-rail">
          <div className="flex items-end justify-between mb-4 gap-3">
            <div>
              <span className="text-[10px] tracking-[0.28em] uppercase text-terracotta font-bold">Today in Black history</span>
              <h2 className="font-heading text-2xl md:text-3xl text-forest mt-1" data-testid="today-history-title">
                {history.title}
              </h2>
              <p className="text-sm text-muted_ink mt-1 max-w-3xl">{history.blurb}</p>
            </div>
            <Link
              to="/movies/black-american"
              data-testid="today-history-see-all"
              className="hidden md:inline-flex items-center gap-1 text-xs font-semibold text-forest hover:text-terracotta shrink-0"
            >
              All 320+ films <ArrowRight size={11} weight="bold" />
            </Link>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2.5" data-testid="today-history-grid">
            {picks.map((p) => {
              const href = `/movies/black-american?film=${encodeURIComponent(p.id)}`;
              // Touch-start prefetch: kick off the destination page's
              // data + chunks the moment the user's finger touches the
              // card, not when their tap lifts. Saves ~150–250ms of
              // perceived latency on mobile because the API call has a
              // head start. We use an inline <link rel="prefetch"> tag
              // appended to <head> — browsers schedule the request
              // automatically and dedupe identical URLs.
              const prefetch = () => {
                try {
                  const id = `prefetch-${p.id}`;
                  if (document.getElementById(id)) return;
                  const link = document.createElement("link");
                  link.id = id;
                  link.rel = "prefetch";
                  link.href = href;
                  document.head.appendChild(link);
                } catch (_) { /* noop */ }
              };
              return (
                <Link
                  key={p.id}
                  to={href}
                  onTouchStart={prefetch}
                  onMouseEnter={prefetch}
                  data-testid={`today-history-pick-${p.id}`}
                  className="group bg-white border border-cream-300 rounded-xl overflow-hidden hover:border-terracotta transition-all"
                >
                  <div className="aspect-[2/3] bg-ink relative">
                    <MediaThumb
                      youtube_id={p.youtube_id}
                      poster={p.poster}
                      title={p.title}
                      subtitle={p.year}
                      kind="movie"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/0 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-1.5 text-cream">
                      <p className="font-heading text-[11px] leading-tight line-clamp-2">{p.title}</p>
                      <p className="text-[9px] opacity-75">{p.year}</p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </>
  );
}
